import { useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom-v5-compat';

import Global from '../../../store/global-selectors';
import { useCapabilityContext } from '../capabilities/use-capability-context';
import { useLLMActionRegistryContext } from '../llm-action-registry/llm-action-registry-context';
import { sendShippyMessage } from '../shippy.api';

/**
 * @typedef {import('../shippy.api').ShippyCapabilityResponse} ShippyCapabilityResponse
 * @typedef {import('../shippy.api').ShippyClarificationResponse} ShippyClarificationResponse
 */

/**
 * Minimal shape the Shippy API expects for each history entry.
 * Callers may pass richer objects — only role + content are sent to the backend.
 * @typedef {object} ApiHistoryMessage
 * @property {'User' | 'Assistant' | string} role - The role of the message sender
 * @property {string} content - The content of the message
 */

/**
 * @typedef {object} ShippyApiFullResponse
 * @property {string} message - The assistant message content
 * @property {ShippyCapabilityResponse} [capability] - Optional capability response
 * @property {ShippyClarificationResponse} [clarification] - Optional clarification request
 * @property {{ commandId: string, parameters?: Record<string, string> }} [command] - Optional legacy command
 */

/**
 * @typedef {object} UseShippyAssistantReturn
 * @property {(message: string, conversationHistory: ApiHistoryMessage[], turnNumber: number, conversationId: string) => Promise<ShippyApiFullResponse>} callShippyApi - Send message with caller-owned state
 * @property {(selector: string) => void} highlightElement - Highlight an element on the page
 */

/**
 * Stateless API hook for Shippy Assistant.
 * Gathers Redux context and exposes callShippyApi — callers own all conversation state.
 * @returns {UseShippyAssistantReturn}
 */
export const useShippyAssistant = () => {
  const location = useLocation();

  const stores = useSelector(Global.selectSellerActiveStores);
  const marketplaces = useSelector(Global.selectReferenceDatagetMarketplaces);
  const sellerFeatures = useSelector(Global.selectSellerSellerFeatures);
  const userRoles = useSelector(Global.selectUseruserRoles);

  const { buildAllContexts } = useCapabilityContext();
  const registry = useLLMActionRegistryContext();

  const connectedStoresContext = useMemo(
    () =>
      stores.map((store) => {
        const marketplace = marketplaces?.find((m) => m.marketplaceId === store.marketplaceId);
        return {
          name: store.name,
          storeGuid: store.storeGuid,
          marketplaceName: marketplace?.name ?? 'Unknown',
        };
      }),
    [stores, marketplaces],
  );

  /**
   * Call the Shippy API with caller-provided conversation state.
   * @param {string} message - The user's message
   * @param {ApiHistoryMessage[]} conversationHistory - Full conversation history (caller-owned)
   * @param {number} turnNumber - 1-based turn number (caller-owned)
   * @param {string} conversationId - Conversation session ID (caller-owned)
   * @returns {Promise<ShippyApiFullResponse>}
   */
  const callShippyApi = useCallback(
    async (message, conversationHistory, turnNumber, conversationId) => {
      const capabilityContexts = buildAllContexts();
      const context = {
        currentRoute: window.location.pathname,
        connectedStores: connectedStoresContext,
      };

      const registryCapabilities = {};
      for (const tool of registry.getToolCatalog()) {
        const viewContext = registry.getViewContext(tool.id);
        const staticContext = capabilityContexts[tool.id];
        registryCapabilities[tool.id] = {
          id: tool.id,
          name: tool.name,
          description: tool.description,
          category: tool.category,
          parameters: tool.parameters,
          ...((staticContext || viewContext) && {
            context: { ...staticContext, ...viewContext },
          }),
        };
      }

      const apiResponse = await sendShippyMessage(
        message,
        context,
        conversationHistory,
        sellerFeatures,
        capabilityContexts,
        userRoles,
        conversationId,
        turnNumber,
        registryCapabilities,
      );

      const { message: responseMessage, clarification, capability, command } = apiResponse;

      return {
        message: responseMessage,
        capability,
        clarification,
        command,
      };
    },
    [buildAllContexts, connectedStoresContext, registry, sellerFeatures, userRoles],
  );

  /**
   * Highlight an element on the page
   * @param {string} selector - CSS selector for the element to highlight
   */
  const highlightElement = useCallback(
    /** @param {string} selector */
    (selector) => {
      if (!selector) {
        return;
      }

      const element = document.querySelector(selector);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });

        element.classList.add('shippy-highlight');

        setTimeout(() => {
          element.classList.remove('shippy-highlight');
        }, 5000);
      }
    },
    [],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const selector = params.get('shippy-highlight');

    if (selector) {
      setTimeout(() => {
        highlightElement(selector);
      }, 500);
    }
  }, [location.pathname, location.search, highlightElement]);

  return {
    callShippyApi,
    highlightElement,
  };
};
